import { useMemo } from "react";
import { Pressable, StyleSheet, Text } from "react-native";

import { busyAria } from "../a11y";
import type { UiTheme } from "../theme";
import { makeSetupStyles } from "./setupStyles";

// One entry of «New program»: a title and the line that says what it does,
// or why it is off.
export function StartEntry({
  theme,
  title,
  body,
  onPress,
  disabled = false,
  busy = false,
}: {
  theme: UiTheme;
  title: string;
  body: string;
  onPress: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  const setup = useMemo(() => makeSetupStyles(theme), [theme]);
  const styles = useMemo(() => makeStyles(theme), [theme]);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${title}. ${body}`}
      accessibilityState={{ disabled, busy }}
      // RNW drops accessibilityState: these reach the web.
      aria-disabled={disabled}
      {...busyAria(busy)}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => [styles.entry, pressed && styles.pressed]}
    >
      <Text style={disabled ? styles.titleOff : styles.title}>{title}</Text>
      {/* The body is what changes loading, error, empty, ready: said aloud. */}
      <Text style={setup.hint} accessibilityLiveRegion="polite" aria-live="polite">
        {body}
      </Text>
    </Pressable>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    entry: {
      minHeight: 44,
      padding: s.s4,
      gap: s.s1,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
    },
    pressed: { backgroundColor: p.surface2 },
    title: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "700" },
    // Nothing to open: quieter, and its line says why.
    titleOff: { color: p.ink3, fontSize: ty.sizes.base, fontWeight: "700" },
  });
}
